import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

/* --------------------------------------------------------------------------
 * TicketTitleContext — Header del hallazgo.
 *
 * Estructura:
 *   Fila 1 eyebrow:  [🏢 Sucursal pill]                 [actionsSlot ⋮]
 *   Fila 2:          H1 titulo de la categoria
 *
 * El #ID y el Estado vivieron antes en un cluster derecho de este header,
 * pero se movieron al `TicketDetailsCard` del aside lateral para concentrar
 * la metadata tecnica en un solo panel. Aca solo queda lo descriptivo:
 * tienda + titulo.
 * -------------------------------------------------------------------------- */
const TicketTitleContext = forwardRef(({ data, actionsSlot, primaryAction }, ref) => {
    const { t } = useTranslation()
    const { id, props } = data
    const store = props?.store
    const ticket_category_primary = props?.ticket_category_primary
    const ticket_category_secondary = props?.ticket_category_secondary

    const title =
        ticket_category_secondary?.label?.es ||
        ticket_category_primary?.label?.es ||
        `${t('ticket_alias', 'Hallazgo')} #${id}`

    const hasTopRow = store?.name || actionsSlot || primaryAction

    return (
        <div ref={ref} className="mt-3 mb-2 md:mt-4">
            {hasTopRow && (
                <div className="flex items-center justify-between gap-3 mb-1.5 min-h-[20px]">
                    {store?.name ? (
                        <Link
                            to={`/stores/${store.id}`}
                            data-clarity-event="navigate-store-from-ticket"
                            title={t('viewStore', 'Ver tienda')}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-[12px] font-medium text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors min-w-0 max-w-full"
                        >
                            <HiOutlineOfficeBuilding className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{store.name}</span>
                        </Link>
                    ) : <span />}
                    {(primaryAction || actionsSlot) && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* primaryAction: CTA de notificacion ("Marcar como leido").
                                El caller (TicketDetail) usa useResponsive para pasar
                                este slot solo en desktop; en mobile renderiza sticky
                                al pie. Evitamos variantes md:* responsive aca por
                                problemas historicos de compile en archivos nuevos. */}
                            {primaryAction}
                            {actionsSlot}
                        </div>
                    )}
                </div>
            )}

            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight tracking-tight">
                {title}
            </h1>
        </div>
    )
})

TicketTitleContext.displayName = 'TicketTitleContext'

export default TicketTitleContext
